import React, { createContext, FC, ReactNode, useContext } from 'react'
import { ServerExperiments } from '@/experimentation'
import { PhaseSlugForTracking } from '@/services/ProjectPhaseService'
import { ShopifyProductWithBundles } from '@/services/Shopify/ShopifyService'
import { SweepstakeObject } from '@/types/codegen-federation'
import {
  GalleryImage,
  GiveawayTabEnum,
  GiveawayType,
} from '@/views/PreSales/PreSalesGiveawayView/hooks/useGiveawayConfigData'
import { useGiveawayState } from '../hooks/useGiveawayState'

interface GiveawayContextValue {
  // State
  selectedIndex: number
  setSelectedIndex: (index: number) => void
  isBackgroundVideoPaused: boolean
  togglePauseBackgroundVideo: () => void
  isShareModalOpen: boolean
  setIsShareModalOpen: (open: boolean) => void
  isScenePlayerOpen: boolean
  setIsScenePlayerOpen: (open: boolean) => void

  // Data
  theatricalSlug: string
  tickets: ShopifyProductWithBundles[] | null
  sweepstake: SweepstakeObject
  phase: PhaseSlugForTracking
  giveawayData: GiveawayType
  projectName: string
  backgroundVideoUrl?: string
  galleryImages: GalleryImage[]
  experiments?: ServerExperiments
  releaseDate: string | null

  // Computed values
  isActive: boolean
  entryVariantId: string

  // Actions
  scrollToProducts: () => void
  scrollToPrizes: () => void
  scrollToDetails: () => void
  scrollToFaq: () => void
  scrollToTab: (tabType: GiveawayTabEnum) => void
  handleRefundInfoClick: () => void

  // Refs (for layout components)
  tabsContainerRef: React.RefObject<HTMLDivElement>
  mobileTicketContainerRef: React.RefObject<HTMLDivElement>
  desktopTicketContainerRef: React.RefObject<HTMLDivElement>
  heroContainerRef: React.RefObject<HTMLDivElement>
  detailsContainerRef: React.RefObject<HTMLDivElement>
  prizesContainerRef: React.RefObject<HTMLDivElement>
  faqContainerRef: React.RefObject<HTMLDivElement>
}

const GiveawayContext = createContext<GiveawayContextValue | null>(null)

export const useGiveawayContext = () => {
  const context = useContext(GiveawayContext)
  if (!context) {
    throw new Error('useGiveawayContext must be used within GiveawayContextProvider')
  }
  return context
}

interface GiveawayContextProviderProps {
  children: ReactNode
  theatricalSlug: string
  tickets: ShopifyProductWithBundles[] | null
  sweepstake: SweepstakeObject
  phase: PhaseSlugForTracking
  giveawayData: GiveawayType
  experiments?: ServerExperiments
  releaseDate: string | null
}

export const GiveawayContextProvider: FC<GiveawayContextProviderProps> = ({
  children,
  theatricalSlug,
  tickets,
  sweepstake,
  phase,
  giveawayData,
  experiments,
  releaseDate,
}) => {
  const giveawayState = useGiveawayState({
    theatricalSlug,
    tickets,
    sweepstake,
    phase,
    giveawayData,
    experiments,
    releaseDate,
  })

  return <GiveawayContext.Provider value={giveawayState}>{children}</GiveawayContext.Provider>
}
